import React from 'react';
import './portalLoading.css';

const repeat = (count) => Array.from({ length: count }, (_, index) => index);
const Block = ({ shape = 'line', short = false }) => <span className={`portal-placeholder portal-placeholder-${shape}${short ? ' portal-placeholder-short' : ''}`} />;

export function LoadingRegion({ label, children, className = '' }) {
    if (!label) return <div className={`portal-loading ${className}`} aria-hidden="true">{children}</div>;
    return <div className={`portal-loading ${className}`} role="status" aria-label={label.replace(/…$|\.\.\.$/, '')} aria-live="polite" aria-busy="true">
        <p className="portal-loading-caption"><span className="portal-loading-dots" aria-hidden="true"><i /><i /><i /></span>{label}</p>
        <div aria-hidden="true">{children}</div>
    </div>;
}

function Cell({ kind }) {
    if (kind === 'cover') return <Block shape="cover" />;
    if (kind === 'selection' || kind === 'actions') return <Block shape={kind === 'selection' ? 'check' : 'pill'} />;
    if (kind === 'book' || kind === 'person') return <div className="portal-placeholder-identity"><Block shape={kind === 'book' ? 'cover' : 'avatar'} /><div><Block /><Block short /></div></div>;
    if (kind === 'status') return <Block shape="pill" />;
    return <div className="portal-placeholder-lines"><Block /><Block short /></div>;
}

export function TableSkeleton({ columns = ['Name', 'Details', 'Status', 'Actions'], rows = 5, label = 'Loading records…', footer = true }) {
    return <LoadingRegion label={label}>
        <div className="portal-loading-table-wrap"><table className="portal-loading-table" style={{ minWidth: `${Math.max(36, columns.length * 8)}rem` }}>
            <thead><tr>{columns.map((column, index) => <th key={index}>{typeof column === 'string' ? column : column.label}</th>)}</tr></thead>
            <tbody>{repeat(rows).map((row) => <tr key={row}>{columns.map((column, index) => <td key={index}><Cell kind={column.kind || (/status|availability|visibility/i.test(column.label || column) ? 'status' : /actions/i.test(column.label || column) ? 'actions' : undefined)} /></td>)}</tr>)}</tbody>
        </table></div>
        {footer && <div className="portal-loading-pagination"><Block short /><div><Block shape="check" /><Block shape="check" /><Block shape="check" /></div></div>}
    </LoadingRegion>;
}

export function MetricsSkeleton({ labels = ['Total', 'Active', 'Pending'], label = 'Loading summary…' }) {
    return <LoadingRegion label={label}><div className="portal-loading-metrics">{labels.map((item) => <div className="portal-loading-metric" key={item}><Block shape="avatar" /><div><span className="portal-loading-label">{item}</span><Block shape="value" /></div></div>)}</div></LoadingRegion>;
}

export function ChartSkeleton({ label = 'Loading chart…' }) {
    return <LoadingRegion label={label}><div className="portal-loading-chart">
        <div className="portal-loading-chart-axis">{repeat(5).map((item) => <Block short key={item} />)}</div>
        <div className="portal-loading-chart-plot"><div className="portal-loading-chart-grid">{repeat(5).map((item) => <i key={item} />)}</div>
            <svg viewBox="0 0 600 220" preserveAspectRatio="none"><path d="M0 175 C55 175 50 120 100 125 S160 185 200 135 S250 155 300 95 S355 145 400 80 S455 105 500 50 S555 75 600 25" /><path className="portal-loading-chart-comparison" d="M0 190 C55 185 60 155 100 160 S160 130 200 170 S250 125 300 140 S355 95 400 120 S455 70 500 100 S555 60 600 80" /></svg>
        </div>
        <div className="portal-loading-chart-ticks">{repeat(7).map((item) => <Block short key={item} />)}</div>
    </div></LoadingRegion>;
}

export function SettingsSkeleton({ label = 'Loading settings…', count = 4, toggles = true }) {
    return <LoadingRegion label={label}><div className="portal-loading-settings">{repeat(count).map((item) => <div className="portal-loading-card" key={item}>
        <div className="portal-loading-card-heading"><Block shape="avatar" /><div><Block /><Block short /></div>{toggles && <Block shape="toggle" />}</div>
        <Block /><div className="portal-loading-field"><Block short /><Block shape="input" /></div>
    </div>)}</div></LoadingRegion>;
}

export function ListSkeleton({ label = 'Loading items…', count = 3 }) {
    return <LoadingRegion label={label}><div className="portal-loading-stack">{repeat(count).map((item) => <div className="portal-loading-list-item" key={item}>
        <Block shape="avatar" /><div className="portal-placeholder-lines"><Block /><Block short /></div><Block shape="pill" /><Block shape="check" />
    </div>)}</div></LoadingRegion>;
}

export function PermissionsSkeleton({ label = 'Loading roles and permissions…' }) {
    return <LoadingRegion label={label}><div className="portal-loading-settings">{['Roles', 'Permissions'].map((section) => <div className="portal-loading-card" key={section}>
        <span className="portal-loading-label">{section}</span><ListSkeleton label={null} />
    </div>)}</div></LoadingRegion>;
}

export function ProfileSkeleton({ label = 'Loading profile and account details…' }) {
    return <LoadingRegion label={label}><div className="portal-loading-profile">
        <div className="portal-loading-card portal-loading-profile-nav"><Block shape="avatar" />{repeat(4).map((item) => <Block key={item} />)}</div>
        <div className="portal-loading-card"><div className="portal-loading-card-heading"><Block shape="avatar" /><div><Block /><Block short /></div></div><FormFields /></div>
    </div></LoadingRegion>;
}

function FormFields() {
    return <><div className="portal-loading-settings">{repeat(4).map((item) => <div className="portal-loading-field" key={item}><Block short /><Block shape="input" /></div>)}</div><Block shape="button" /></>;
}

export function FormSkeleton({ label = 'Loading form details…' }) {
    return <LoadingRegion label={label}><div className="portal-loading-card"><Block /><Block short /><FormFields /></div></LoadingRegion>;
}

export function CardsSkeleton({ label = 'Loading books…', kind = 'book', count = 4 }) {
    return <LoadingRegion label={label}><div className={`portal-loading-cards portal-loading-cards-${kind}`}>{repeat(count).map((item) => <div className="portal-loading-card" key={item}>
        {kind === 'book' ? <Block shape="book" /> : <div className="portal-loading-voucher"><Block shape="value" /><Block shape="pill" /></div>}
        <Block /><Block short /><div className="portal-loading-card-heading"><Block shape="pill" /><Block shape="pill" /></div><Block shape="button" />
    </div>)}</div></LoadingRegion>;
}

export function RevenueSkeleton({ compact = false, publisher = false }) {
    return <LoadingRegion label="Loading revenue by currency…"><div className="portal-loading-stack"><MetricsSkeleton label={null} labels={publisher ? ['Total income', 'Payments received', 'Remaining income'] : ['Total sales', 'Akello share']} />
        <MetricsSkeleton label={null} labels={['From purchases', 'From subscriptions']} />
        {!compact && <TableSkeleton label={null} columns={publisher ? ['Currency', 'Total income', 'Payments received', 'Remaining income'] : ['Currency', 'Total sales', 'Akello share']} rows={3} footer={false} />}
    </div></LoadingRegion>;
}

export default function PortalSkeleton({ variant = 'table', ...props }) {
    const Component = { table: TableSkeleton, metrics: MetricsSkeleton, chart: ChartSkeleton, settings: SettingsSkeleton, list: ListSkeleton, permissions: PermissionsSkeleton, profile: ProfileSkeleton, form: FormSkeleton, books: CardsSkeleton, vouchers: CardsSkeleton, revenue: RevenueSkeleton }[variant] || TableSkeleton;
    return <Component {...props} {...(variant === 'vouchers' ? { kind: 'voucher' } : {})} />;
}
